export function inrFull(n) {
  const v = Math.round(Number.isFinite(n) ? n : 0);
  return "₹" + v.toLocaleString("en-IN");
}

// Up to 2 decimals where precision matters (e.g. EMI); whole rupees stay clean.
export function inrPrecise(n) {
  const v = Number.isFinite(n) ? n : 0;
  return "₹" + new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
}

// Comma-grouped (en-IN) editable amount fields: state stores RAW digits only,
// display is formatted. Typing/deleting/pasting never carries commas into math.
export function formatAmountInput(raw) {
  const digits = String(raw ?? "").replace(/[^\d]/g, "").slice(0, 12);
  if (!digits) return "";
  return new Intl.NumberFormat("en-IN").format(Number(digits));
}

export function parseAmountInput(formatted) {
  return String(formatted ?? "").replace(/[^\d]/g, "");
}

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function twoDigitWords(n) {
  if (n < 20) return ONES[n];
  return TENS[Math.floor(n / 10)] + (n % 10 ? " " + ONES[n % 10] : "");
}

function threeDigitWords(n) {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return [h ? `${ONES[h]} Hundred` : "", rest ? twoDigitWords(rest) : ""].filter(Boolean).join(" ");
}

// Indian numbering words: Thousand / Lakh / Crore (and beyond, recursively).
export function numToWordsIndian(value) {
  const n = Math.floor(Math.abs(Number(value)));
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "Zero";
  let rem = n;
  const crore = Math.floor(rem / 1e7); rem %= 1e7;
  const lakh = Math.floor(rem / 1e5); rem %= 1e5;
  const thousand = Math.floor(rem / 1e3); rem %= 1e3;
  const parts = [];
  if (crore) parts.push(`${crore > 99 ? numToWordsIndian(crore) : twoDigitWords(crore)} Crore`);
  if (lakh) parts.push(`${twoDigitWords(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigitWords(thousand)} Thousand`);
  if (rem) parts.push(threeDigitWords(rem));
  return parts.join(" ");
}

// "Five Lakh Rupees Only" — or a custom suffix like "Rupees per month" for EMI.
// Returns "" for empty/invalid input so callers can show a placeholder instead.
export function moneyWords(value, suffix = "Rupees Only") {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${numToWordsIndian(Math.round(n))} ${suffix}`.trim();
}

export function inrShort(n) {
  const v = Number.isFinite(n) ? n : 0;
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹${trim(v / 1e7)} Cr`;
  if (abs >= 1e5) return `₹${trim(v / 1e5)} L`;
  return inrFull(v);
}

function trim(x) {
  return x.toFixed(2).replace(/\.?0+$/, "");
}

export function calcEmi(principal, annualRate, months) {
  const P = Number(principal) || 0;
  const n = Math.max(1, Math.round(Number(months) || 1));
  const r = (Number(annualRate) || 0) / 12 / 100;
  if (r === 0) {
    const emi0 = P / n;
    return { emi: emi0, totalPayment: P, totalInterest: 0, principalShare: 100 };
  }
  const pow = Math.pow(1 + r, n);
  const emi = (P * r * pow) / (pow - 1);
  const totalPayment = emi * n;
  const totalInterest = totalPayment - P;
  return {
    emi,
    totalPayment,
    totalInterest,
    principalShare: totalPayment > 0 ? (P / totalPayment) * 100 : 100,
  };
}

export function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
